/**
 * Pricing page — public-facing (admin-gated) AEGIS Pro pricing.
 *
 * Single plan, two billing cycles. Captures the optional ?ref= referral code
 * from the URL and sends it to the backend during Checkout creation.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Sparkles, ArrowRight, BadgePercent, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  'Suivi mots-clés SEO illimités (DataForSEO, Haloscan, SEMrush, Custom)',
  'Tableaux de bord SEA — Google Ads, Meta, LinkedIn',
  'Closed-loop ROI par creative (campagne / ad / form)',
  'Webhooks leads (WordPress, WPForms, Gravity, Zapier)',
  'Détection automatique de doublons par contenu',
  'Portail client public avec qualification des leads',
  'Reporting PDF mensuel automatisé',
  'Gestion multi-consultants (SEO + SEA séparés)',
  'Alertes GBP, Search Console, providers',
  'Comptes utilisateurs illimités',
  'Clients gérés illimités',
];

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [status, setStatus] = useState(null);
  const [referral, setReferral] = useState(null);
  const [refLoading, setRefLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);

  const refCode = searchParams.get('ref');

  useEffect(() => {
    axios.get(`${API}/billing/pricing`).then(r => setPricing(r.data)).catch(() => {});
    axios.get(`${API}/billing/status`, { withCredentials: true }).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!refCode) return;
    setRefLoading(true);
    axios.get(`${API}/billing/referral/validate`, { params: { code: refCode }, withCredentials: true })
      .then(r => setReferral(r.data))
      .catch(() => setReferral({ valid: false, code: refCode }))
      .finally(() => setRefLoading(false));
  }, [refCode]);

  const startCheckout = async (cycle) => {
    setSubmitting(cycle);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        cycle,
        origin_url: window.location.origin,
        referral_code: referral?.valid ? referral.code : undefined,
      }, { withCredentials: true });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur PayPal');
    } finally {
      setSubmitting(null);
    }
  };

  const monthly = pricing?.monthly_eur ?? 100;
  const annual = pricing?.annual_eur ?? 1000;
  const annualMonthly = (annual / 12).toFixed(2);
  const trialDays = pricing?.trial_days ?? 14;

  return (
    <Layout>
      <div className="space-y-8 max-w-5xl mx-auto pb-12" data-testid="pricing-page">
        <div className="text-center pt-4">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-neutral-900">
            AEGIS Pro
          </h1>
          <p className="text-neutral-600 mt-3 max-w-xl mx-auto">
            Tout-en-un pour piloter votre agence SEO/SEA. Un prix simple, sans frais cachés.
            Essai gratuit {trialDays} jours, sans CB.
          </p>
        </div>

        {/* Active subscription banner */}
        {(status?.subscription_status === 'active' || status?.subscription_status === 'trialing') && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-900">
            ✓ Vous avez déjà un abonnement <strong>{status.cycle === 'year' ? 'annuel' : 'mensuel'}</strong> {status.subscription_status === 'trialing' ? '(en essai)' : 'actif'}.
            <button onClick={() => navigate('/billing')} className="underline ml-1">Gérer mon abonnement</button>
          </div>
        )}

        {/* Referral banner */}
        {refCode && (
          <div className="border border-violet-200 bg-violet-50 rounded-lg p-4 flex items-center gap-3">
            {refLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
            ) : referral?.valid ? (
              <>
                <BadgePercent className="w-5 h-5 text-violet-600 shrink-0" />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-violet-900">
                    Code parrainage <span className="font-mono">{referral.code}</span> appliqué
                  </div>
                  <div className="text-violet-800/80 text-[12px]">
                    Parrainé par <strong>{referral.referrer_name}</strong> · vous bénéficiez de <strong>-50% sur le 1er mois</strong>.
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-amber-800">
                Code parrainage <span className="font-mono">{refCode}</span> introuvable. Vérifiez le lien partagé.
              </div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly card */}
          <PriceCard
            label="Mensuel"
            price={`${monthly} €`}
            sub="HT / mois — sans engagement"
            cta="Démarrer l'essai 14 jours"
            highlight={false}
            loading={submitting === 'monthly'}
            disabled={status?.is_active}
            onClick={() => startCheckout('monthly')}
            features={FEATURES}
            testid="checkout-monthly-btn"
          />

          {/* Annual card */}
          <PriceCard
            label="Annuel — 2 mois offerts"
            badge="Économisez 200 €"
            price={`${annual} €`}
            sub={`HT / an — soit ${annualMonthly} €/mois`}
            cta="Démarrer l'essai 14 jours"
            highlight
            loading={submitting === 'annual'}
            disabled={status?.is_active}
            onClick={() => startCheckout('annual')}
            features={FEATURES}
            testid="checkout-annual-btn"
          />
        </div>

        <div className="text-[12px] text-neutral-500 text-center max-w-2xl mx-auto leading-relaxed">
          {pricing?.invoice_footer ||
            "Société Aegis Développement, basée en Principauté d'Andorre. Facturation HT (auto-liquidation pour les preneurs assujettis dans l'UE)."}
        </div>
      </div>
    </Layout>
  );
}

function PriceCard({ label, badge, price, sub, cta, highlight, features, onClick, loading, disabled, testid }) {
  return (
    <div className={`rounded-2xl border p-6 flex flex-col ${highlight ? 'border-[#002FA7] shadow-md bg-white' : 'border-neutral-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className={`font-heading text-lg ${highlight ? 'text-[#002FA7]' : 'text-neutral-900'}`}>{label}</h3>
        {badge && (
          <span className="text-[10px] uppercase tracking-wide bg-[#002FA7] text-white px-2 py-1 rounded-full font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {badge}
          </span>
        )}
      </div>
      <div className="text-4xl font-bold tabular-nums mt-3">{price}</div>
      <div className="text-sm text-neutral-500 mt-1">{sub}</div>
      <ul className="mt-6 space-y-2 text-[13px] flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? 'text-[#002FA7]' : 'text-emerald-600'}`} />
            <span className="text-neutral-700">{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        disabled={loading || disabled}
        className={`mt-6 w-full ${highlight ? 'bg-[#002FA7] hover:bg-[#002277] text-white' : ''}`}
        variant={highlight ? 'default' : 'outline'}
        data-testid={testid}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {disabled ? 'Abonnement déjà actif' : cta}
        {!loading && !disabled && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
    </div>
  );
}
